import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flag } from 'lucide-react';
import { useArrowKeyFormNav } from '../hooks/useArrowKeyFormNav';
import FloatingResizablePanel from './FloatingResizablePanel';
import DraggableModal from './DraggableModal';
import UtrTrailModal from './UtrTrailModal';
import ClientFieldHistoryPanel from './ClientFieldHistoryPanel';
import { onHeaderLabelsUpdated, offHeaderLabelsUpdated } from '../utils/socket';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Session-43: Pending Requests chooser as a draggable sheet.
 * Opens ~30% from top so it doesn't collide with the mobile keyboard, and
 * users can drag the header bar to reposition anywhere on the screen. The
 * request-list area scrolls internally when the count exceeds the visible
 * body height.
 */
const DraggablePendingChooser = ({ kind, items, onClose, onApprove, onCancel }) => {
  // Vertical offset in pixels from the initial position. Starts at 0 and
  // moves +/- as user drags the handle. Clamped so the sheet can't leave
  // the viewport.
  const [offsetY, setOffsetY] = React.useState(0);
  const dragRef = React.useRef({ dragging: false, startY: 0, startOffset: 0 });

  const onPointerDown = (e) => {
    dragRef.current = {
      dragging: true,
      startY: e.touches ? e.touches[0].clientY : e.clientY,
      startOffset: offsetY,
    };
    e.preventDefault();
  };
  React.useEffect(() => {
    const move = (e) => {
      if (!dragRef.current.dragging) return;
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      const dy = y - dragRef.current.startY;
      const next = dragRef.current.startOffset + dy;
      // Clamp: don't allow sheet to go above viewport OR below its own height
      const maxUp = -Math.round(window.innerHeight * 0.25);
      const maxDown = Math.round(window.innerHeight * 0.4);
      setOffsetY(Math.min(maxDown, Math.max(maxUp, next)));
    };
    const up = () => { dragRef.current.dragging = false; };
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', move, { passive: true });
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 z-[110]"
         onClick={onClose}
         data-testid="sr-pending-chooser-backdrop">
      <div
        onClick={(e) => e.stopPropagation()}
        className="absolute left-1/2 -translate-x-1/2 bg-white rounded-xl w-[95%] max-w-md shadow-2xl select-none"
        style={{
          // Start at ~28% from top → "center se thoda upar"
          top: `calc(28% + ${offsetY}px)`,
          maxHeight: '65vh',
          display: 'flex',
          flexDirection: 'column',
        }}
        data-testid="sr-pending-chooser">
        {/* Drag handle bar */}
        <div
          onMouseDown={onPointerDown}
          onTouchStart={onPointerDown}
          className="cursor-grab active:cursor-grabbing py-2 flex items-center justify-center border-b border-slate-100"
          data-testid="sr-pending-drag-handle"
        >
          <div className="w-10 h-1 rounded-full bg-slate-300" />
        </div>
        <div className="flex items-center justify-between px-4 pt-2 pb-2">
          <h3 className="font-bold text-base">
            {kind === 'coin_cut' ? '✂️ Pending Withdraw Requests' : '💰 Pending Deposit Requests'}
          </h3>
          <button onClick={onClose}
                  className="text-slate-400 hover:text-slate-700 text-xl leading-none px-1"
                  data-testid="sr-pending-close">×</button>
        </div>
        <p className="text-[11px] text-slate-500 px-4 mb-2">
          Pick one — Approve to send money, or Cancel with a reason. Drag the top bar to reposition.
        </p>
        <div className="overflow-y-auto space-y-2 px-4 pb-4 flex-1">
          {items.map(req => (
            <div key={req.id}
                 className="border border-slate-200 rounded-lg p-3 bg-slate-50"
                 data-testid={`sr-pending-item-${req.request_no}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold text-slate-800 text-sm">{req.request_no}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wide">{req.type}</div>
              </div>
              <div className="text-[12px] text-slate-700 space-y-0.5 mb-3">
                {/* Session-48: amount hidden from chooser — handler will
                    decide the actual amount inside the modal. Client's
                    requested amount is not relevant to the approval choice. */}
                {req.reference && <div>Reference: {req.reference}</div>}
                {req.account_snapshot && (req.account_snapshot.account_number || req.account_snapshot.bank || req.account_snapshot.upi_id) && (
                  <div className="text-[11px] text-slate-500">
                    Account: {req.account_snapshot.bank || ''} {req.account_snapshot.account_number ? `· A/c ${req.account_snapshot.account_number}` : ''}{req.account_snapshot.upi_id ? ` · UPI ${req.account_snapshot.upi_id}` : ''}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onApprove(req)}
                  className="flex-1 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold"
                  data-testid={`sr-approve-${req.request_no}`}
                >✓ Approve</button>
                <button
                  onClick={() => onCancel(req)}
                  className="flex-1 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-bold"
                  data-testid={`sr-cancel-${req.request_no}`}
                >✗ Cancel</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const BLANK_BANK = {
  account_holder_name: '',
  bank_account_number: '',
  ifsc_code: '',
  bank_name: '',
  upi_id: '',
};

// Well-known bank keywords → normalised bank names. Used when the pasted form
// doesn't have an explicit "BANK NAME:" line but the IFSC / UPI hints at one.
const BANK_KEYWORDS = [
  ['state bank of india', 'State Bank of India'],
  ['sbi', 'State Bank of India'],
  ['hdfc', 'HDFC Bank'],
  ['icici', 'ICICI Bank'],
  ['axis', 'Axis Bank'],
  ['kotak', 'Kotak Mahindra Bank'],
  ['punjab national', 'Punjab National Bank'],
  ['pnb', 'Punjab National Bank'],
  ['bank of baroda', 'Bank of Baroda'],
  ['canara', 'Canara Bank'],
  ['union bank', 'Union Bank of India'],
  ['yes bank', 'Yes Bank'],
  ['idfc', 'IDFC First Bank'],
  ['indusind', 'IndusInd Bank'],
  ['idbi', 'IDBI Bank'],
  ['central bank', 'Central Bank of India'],
];

/**
 * Extract bank fields from a pasted blob (WhatsApp form dumps, etc.).
 * Handles patterns like:
 *   *ACCOUNT NO:*41939127506
 *   NAME: Parth Raval
 *   IFSC CODE: SBIN0016033
 *   BANK: State Bank of India
 *   UPI I'D — ravalparth578@oksbi
 * Only returns non-empty fields so callers can merge without wiping user input.
 */
const parseBankFromPaste = (raw) => {
  if (!raw || typeof raw !== 'string') return {};
  // Normalise — strip markdown asterisks, weird bullets, CRs
  const text = raw.replace(/\*/g, ' ').replace(/[·•●]/g, ' ').replace(/[\r]+/g, '\n');
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

  const out = {};

  // Try each label in order until one hits — returns the value AFTER the label
  // + colon/dash separator. Case-insensitive but position-sensitive so more
  // specific labels win over generic ones.
  const labelMatch = (line, labels) => {
    const low = line.toLowerCase();
    for (const lbl of labels) {
      const idx = low.indexOf(lbl);
      if (idx !== -1) {
        // Guard: the char just before the label must be non-alphabetic so we
        // don't match "Name" inside "User Name" for the account_holder pass.
        // (Callers can still opt into loose matching by allowing 'name'.)
        const prev = idx === 0 ? '' : low[idx - 1];
        if (prev && /[a-z]/.test(prev)) continue;
        let rest = line.slice(idx + lbl.length);
        rest = rest.replace(/^[\s:\-–—=]+/, '').trim();
        // Cut at first double-space (multi-field lines)
        rest = rest.split(/\s{2,}/)[0].trim();
        if (rest) return rest;
      }
    }
    return null;
  };

  // Pass 1 — account holder name.
  // Strict labels first (won't clash with "User Name" / "Bank Name"), then a
  // loose "name" match that skips lines starting with those false-positives.
  for (const line of lines) {
    if (out.account_holder_name) break;
    const strict = labelMatch(line, ['a/c holder name', 'account holder name', 'holder name', 'beneficiary name']);
    if (strict && !/^\d+$/.test(strict)) { out.account_holder_name = strict; break; }
  }
  if (!out.account_holder_name) {
    for (const line of lines) {
      const low = line.toLowerCase().trim();
      // Skip lines whose primary label is NOT the holder name.
      if (/^(user\s*name|bank\s*name|app\s*name|screen\s*name|login\s*name)\b/i.test(low)) continue;
      const v = labelMatch(line, ['name']);
      if (v && !/^\d+$/.test(v)) { out.account_holder_name = v; break; }
    }
  }

  // Pass 2 — bank account number.
  for (const line of lines) {
    if (out.bank_account_number) break;
    const v = labelMatch(line, ['bank account no', 'bank account number', 'account no', 'account number', 'acc no', 'a/c no', 'a/c number']);
    if (v) {
      const digits = v.replace(/\D/g, '');
      if (digits.length >= 6) { out.bank_account_number = digits; break; }
    }
  }

  // Pass 3 — IFSC (label first, whole-text regex as fallback).
  for (const line of lines) {
    if (out.ifsc_code) break;
    const v = labelMatch(line, ['ifsc code', 'ifsc']);
    if (v) {
      const m = v.toUpperCase().match(/[A-Z]{4}0[A-Z0-9]{6}/);
      if (m) { out.ifsc_code = m[0]; break; }
    }
  }
  if (!out.ifsc_code) {
    const m = text.toUpperCase().match(/[A-Z]{4}0[A-Z0-9]{6}/);
    if (m) out.ifsc_code = m[0];
  }

  // Pass 4 — bank name. Only match the specific "bank name" label — NEVER
  // "bank" alone (would eat "bank account no").
  for (const line of lines) {
    if (out.bank_name) break;
    const v = labelMatch(line, ['bank name']);
    if (v && v.length <= 60) { out.bank_name = v; break; }
  }

  // Pass 5 — UPI ID (only if present; user said "if UPI is there fine, else
  // leave blank"). Prefer explicit label, then fallback to xxx@known-suffix.
  for (const line of lines) {
    if (out.upi_id) break;
    const v = labelMatch(line, ["upi i'd", 'upi id', 'upi']);
    if (v && v.includes('@')) { out.upi_id = v.split(/\s+/)[0]; break; }
  }
  if (!out.upi_id) {
    const m = text.match(/[\w.\-]+@[\w.\-]+/);
    if (m && /@(?:ybl|okhdfcbank|okaxis|oksbi|okicici|paytm|apl|ibl|axl|axisbank|hdfcbank|sbi|upi|allbank|federal|kotak|barodampay|indus|jio)\b/i.test(m[0])) {
      out.upi_id = m[0];
    }
  }

  // Bank name: infer from IFSC prefix or keyword scan when the form didn't
  // include a "Bank Name:" line. User specifically wants "SBI" → "State Bank
  // of India" expansion.
  const IFSC_MAP = { SBIN: 'State Bank of India', HDFC: 'HDFC Bank', ICIC: 'ICICI Bank', UTIB: 'Axis Bank', KKBK: 'Kotak Mahindra Bank', PUNB: 'Punjab National Bank', BARB: 'Bank of Baroda', CNRB: 'Canara Bank', UBIN: 'Union Bank of India', YESB: 'Yes Bank', IDFB: 'IDFC First Bank', INDB: 'IndusInd Bank', IBKL: 'IDBI Bank', CBIN: 'Central Bank of India' };
  if (out.bank_name) {
    // Normalise common shorthand → full name so downstream forms are clean.
    const low = out.bank_name.toLowerCase();
    for (const [kw, name] of BANK_KEYWORDS) {
      if (low === kw || low.startsWith(kw)) { out.bank_name = name; break; }
    }
  } else {
    // No bank_name label found — infer.
    if (out.ifsc_code) {
      const code = out.ifsc_code.slice(0, 4).toUpperCase();
      if (IFSC_MAP[code]) out.bank_name = IFSC_MAP[code];
    }
    if (!out.bank_name) {
      const low = text.toLowerCase();
      for (const [kw, name] of BANK_KEYWORDS) {
        // Require word boundary so "sbi" doesn't match inside random text
        const re = new RegExp(`(^|[^a-z])${kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z]|$)`, 'i');
        if (re.test(low)) { out.bank_name = name; break; }
      }
    }
  }

  return out;
};

const ChatHeaderActions = ({ chatType, actorId, actorRole, customerId, roomId, onMessageSent, customerFlag, onFlagChanged, onCreateRequest, onMarkSolved }) => {
  // Session-48g: Custom header-label config fetched from Admin Settings.
  // Empty object falls back to hardcoded defaults. Slots:
  //   { left_create, right_action }  keyed per chat_type × role
  const [hdrLabels, setHdrLabels] = useState({});
  useEffect(() => {
    const t = (chatType === 'withdrawal' ? 'withdraw' : chatType) || '';
    const roleLower = (actorRole || '').toLowerCase();
    const role = (roleLower === 'super_admin' || roleLower === 'admin') ? 'admin' : (roleLower.endsWith('_handler') ? 'handler' : 'customer');
    if (!t) return;
    let cancelled = false;
    axios.get(`${API}/header-labels?type=${t}&role=${role}`)
      .then(r => { if (!cancelled) setHdrLabels(r.data || {}); })
      .catch(() => {});

    // Session-49: live-refresh when admin edits labels in Settings.
    const handler = (payload) => {
      if (!payload) return;
      if (payload.chat_type !== t) return;
      if (payload.role !== role) return;
      // Merge only the fields that were touched so the header repaints
      // instantly without a network round-trip.
      setHdrLabels(prev => ({ ...prev, ...(payload.labels || {}) }));
    };
    onHeaderLabelsUpdated(handler);
    return () => { cancelled = true; offHeaderLabelsUpdated(handler); };
  }, [chatType, actorRole]);
  const [modal, setModal] = useState(null); // 'coin_cut' | 'deposit' | null
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [utrNumber, setUtrNumber] = useState('');
  const [utrCheck, setUtrCheck] = useState({ exists: false, matches: [] });
  const [description, setDescription] = useState('');
  const [receivedIn, setReceivedIn] = useState('');       // OUR bank / UPI where money landed
  const [receivedFrom, setReceivedFrom] = useState('');   // CUSTOMER'S bank / UPI they paid from
  const [bank, setBank] = useState(BLANK_BANK);   // Coin-Cut client-bank preview / override
  const [bankLoading, setBankLoading] = useState(false);
  const [bankSaved, setBankSaved] = useState(false); // hide bank block when saved
  const [busy, setBusy] = useState(false);
  const [flagBusy, setFlagBusy] = useState(false);
  const [utrBlockMode, setUtrBlockMode] = useState('warn'); // 'warn' | 'block'
  const [showUtrTrail, setShowUtrTrail] = useState(false);
  // Coin-Cut: history picker state ({field: 'bank'|'upi'} while open)
  const [historyPicker, setHistoryPicker] = useState(null);
  const [pickedBankEntryId, setPickedBankEntryId] = useState(null);
  const [pickedUpiEntryId, setPickedUpiEntryId] = useState(null);
  // Verified status of the currently-displayed bank / UPI (derived from
  // the active client_field_history entry). Rendered as a small blue ✓ so
  // handlers instantly recognise a bank that money has already gone to.
  const [bankVerified, setBankVerified] = useState({ bank: null, upi: null });

  // Session-41: gating — Coin Cut / Deposit buttons now open a chooser of
  // pending service requests first. Only after handler picks a specific
  // request + Approve action does the amount modal open. Cancel action from
  // the chooser opens a reason-only dialog. Zero-request state shows an
  // instructional toast so nobody sends money without a paper trail.
  const [pendingList, setPendingList] = useState([]);        // open service_requests for this cust+type
  const [pendingKind, setPendingKind] = useState(null);       // 'coin_cut' | 'deposit' — which button was clicked
  const [pendingLoading, setPendingLoading] = useState(false);
  const [approvingReqId, setApprovingReqId] = useState(null); // request being approved when modal is open
  const [cancelForReq, setCancelForReq] = useState(null);     // request obj being cancelled
  const [cancelReason, setCancelReason] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  // Support Mark-as-Solved gate: when an open Support request exists, show an
  // Approve step first; only after Approve does the description dialog open.
  const [supportChooser, setSupportChooser] = useState(null); // null | [reqs]
  const [supportChooserLoading, setSupportChooserLoading] = useState(false);

  // Point 6 — Mark-as-Solved requires an OPEN request. Support shows an
  // Approve chooser (pick the exact request → Point 4); inquiry solves its one
  // open request directly. If none is open, we DO NOT solve — just warn.
  const handleMarkSolvedClick = async () => {
    const ct = String(chatType || '').toLowerCase();
    if (!customerId || (ct !== 'support' && ct !== 'inquiry' && ct !== 'enquiry')) {
      onMarkSolved();
      return;
    }
    const srType = (ct === 'inquiry' || ct === 'enquiry') ? 'inquiry' : 'support';
    setSupportChooserLoading(true);
    try {
      const { data } = await axios.get(`${API}/service-requests/open/${customerId}`, {
        params: { type: srType },
      });
      const items = data?.items || [];
      if (items.length === 0) {
        toast.error('No pending request to approve. Nothing to mark as solved.', { duration: 4000 });
        return;
      }
      if (srType === 'inquiry') { onMarkSolved(items[0].id); return; }
      setSupportChooser(items);
    } catch (_) {
      onMarkSolved(); // fail-open — never block solving on a lookup error
    } finally { setSupportChooserLoading(false); }
  };

  // Fetch global UTR block-mode once — determines whether duplicate UTRs are a
  // soft warning or a hard block on the submit button.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/settings`).then(r => {
      if (!cancelled) setUtrBlockMode(r.data?.utr_block_mode === 'block' ? 'block' : 'warn');
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Fetch client's saved bank details whenever the Coin Cut modal opens
  useEffect(() => {
    if (modal !== 'coin_cut' || !customerId) return;
    let cancelled = false;
    setBankLoading(true);
    setBankVerified({ bank: null, upi: null });
    axios.get(`${API}/client-bank-details/${customerId}`)
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.bank_details || {};
        const filled = {
          account_holder_name: d.account_holder_name || '',
          bank_account_number: d.bank_account_number || '',
          ifsc_code: d.ifsc_code || '',
          bank_name: d.bank_name || '',
          upi_id: d.upi_id || '',
        };
        setBank(filled);
        // Consider bank "saved" when we have at least one identifying field
        setBankSaved(!!(filled.bank_account_number || filled.upi_id || filled.account_holder_name));
      })
      .catch(() => { if (!cancelled) { setBank(BLANK_BANK); setBankSaved(false); } })
      .finally(() => { if (!cancelled) setBankLoading(false); });
    return () => { cancelled = true; };
  }, [modal, customerId]);

  // Load the active bank/UPI history entries so we know whether the currently
  // displayed values are verified. Re-runs when picker fires so a freshly
  // picked entry's verified badge shows up immediately.
  useEffect(() => {
    if (modal !== 'coin_cut' || !customerId) return;
    let cancelled = false;
    const load = async () => {
      const fetchActive = async (field) => {
        try {
          const { data } = await axios.get(`${API}/clients/${customerId}/field-history`, { params: { field } });
          const active = (data?.entries || []).find(e => e.is_active) || null;
          return active;
        } catch { return null; }
      };
      const [bAct, uAct] = await Promise.all([fetchActive('bank'), fetchActive('upi')]);
      if (!cancelled) setBankVerified({ bank: bAct, upi: uAct });
    };
    load();
    return () => { cancelled = true; };
  }, [modal, customerId, pickedBankEntryId, pickedUpiEntryId]);

  // Session-41 gating: open the pending-requests chooser for a button.
  // Handlers must Approve a specific request before the send-money modal
  // opens, or explicitly Cancel one with a written reason.
  const openPendingChooser = async (kind) => {
    const srType = kind === 'coin_cut' ? 'withdraw' : 'deposit';
    setPendingLoading(true);
    setPendingKind(kind);
    try {
      const { data } = await axios.get(`${API}/service-requests/open/${customerId}`, {
        // Coin Cut: hide requests already coin-cut (accepted) so an approved
        // withdraw can't be re-approved. Deposit closes its SR on Deposit Done,
        // so it needs no such filter.
        params: { type: srType, ...(kind === 'coin_cut' ? { exclude_accepted: true } : {}) },
      });
      const items = data?.items || [];
      if (items.length === 0) {
        // Zero-request guardrail. Handler must wait for the client to create
        // a request OR generate one manually via the three-dot menu first.
        setPendingKind(null);
        toast.error(
          srType === 'withdraw'
            ? 'No pending withdraw request. Ask the client to request or create one manually.'
            : 'No pending deposit request. Ask the client to request or create one manually.',
          { duration: 4500 },
        );
        return;
      }
      setPendingList(items);
    } catch (e) {
      setPendingKind(null);
      toast.error(e?.response?.data?.detail || 'Failed to load pending requests');
    } finally { setPendingLoading(false); }
  };

  const approveRequest = (req) => {
    // Session-48e: Full reset when approving so any prior modal state
    // (utr / description / receivedIn / receivedFrom / bank overrides) is
    // wiped. User expectation: every open of the amount modal is a fresh
    // blank form.
    resetFields();
    setApprovingReqId(req.id);
    setModal(pendingKind);
    setPendingList([]);
    setPendingKind(null);
  };

  // Cancel → free-text reason (required, min 1 char). Sends to backend and
  // removes the row from the chooser.
  const submitCancel = async () => {
    if (!cancelForReq) return;
    const reason = (cancelReason || '').trim();
    if (!reason) { toast.error('Please enter a cancellation reason'); return; }
    setCancelBusy(true);
    try {
      await axios.post(`${API}/service-requests/cancel`, {
          request_id: cancelForReq.id, actor_id: actorId, reason,
        });
      toast.success(`❌ ${cancelForReq.request_no} cancelled`);
      setPendingList(prev => prev.filter(r => r.id !== cancelForReq.id));
      // If no requests remain, close the whole chooser.
      setPendingList(prev => {
        const next = prev.filter(r => r.id !== cancelForReq.id);
        if (next.length === 0) setPendingKind(null);
        return next;
      });
      setCancelForReq(null);
      setCancelReason('');
      if (onMessageSent) onMessageSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    } finally { setCancelBusy(false); }
  };

  const setFlag = async (nextFlag) => {
    if (!customerId || flagBusy) return;
    setFlagBusy(true);
    try {
      const res = await axios.patch(`${API}/users/${customerId}/flag`, { actor_id: actorId, flag: nextFlag });
      if (res.data?.approval_required) {
        toast.info('📩 Request sent to admin — flag change will apply after approval', { duration: 4000 });
      } else {
        toast.success(nextFlag ? `Marked ${nextFlag === 'red' ? 'Suspicious' : 'Premium'}` : 'Flag removed');
        if (onFlagChanged) onFlagChanged(nextFlag);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Flag update failed');
    } finally { setFlagBusy(false); }
  };

  const resetFields = () => {
    setAmount('');
    setReference('');
    setUtrNumber('');
    setDescription('');
    setReceivedIn('');
    setReceivedFrom('');
    setBank(BLANK_BANK);
    setPickedBankEntryId(null);
    setPickedUpiEntryId(null);
    setApprovingReqId(null);
  };

  const submit = async () => {
    const clean = String(amount || '').trim().replace(/,/g, '');
    if (!clean || isNaN(Number(clean)) || Number(clean) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    // Deposit-mode: UTR is the only mandatory field. Received In / Received
    // From / Description are all OPTIONAL now — the customer message adapts
    // based on which fields were filled.
    if (modal === 'deposit') {
      const utr = utrNumber.trim();
      if (!utr || !/^[A-Za-z0-9]{6,22}$/.test(utr)) {
        toast.error('Enter a valid UTR number (6–22 chars, letters/digits)');
        return;
      }
    }
    setBusy(true);
    try {
      if (modal === 'coin_cut') {
        const res = await axios.post(`${API}/coin-cut/broadcast`, {
            actor_id: actorId,
            customer_id: customerId,
            amount: clean,
            room_id: roomId,
            reference: reference.trim() || null,
            bank_entry_id: pickedBankEntryId || undefined,
            upi_entry_id: pickedUpiEntryId || undefined,
            bank_override: {
              account_holder_name: bank.account_holder_name.trim(),
              bank_account_number: bank.bank_account_number.trim(),
              ifsc_code: bank.ifsc_code.trim(),
              bank_name: bank.bank_name.trim(),
              upi_id: bank.upi_id.trim(),
            },
          });
        // Server deduped a retry/double-tap — don't claim a fresh success.
        if (res.data?.duplicate_ignored) toast.info('Already processed — refreshing');
        else toast.success('Coin cut sent · client notified · group updated');
      } else {
        const res = await axios.post(`${API}/deposit-done/notify`, {
            actor_id: actorId,
            customer_id: customerId,
            amount: clean,
            room_id: roomId,
            utr_number: utrNumber.trim(),
            received_in: receivedIn.trim(),
            received_from: receivedFrom.trim(),
            description: description.trim(),
            // Legacy field mirrored for older backends that still read `bank_account`
            bank_account: receivedIn.trim(),
          });
        if (res.data?.duplicate_ignored) toast.info('Already processed — refreshing');
        else toast.success('Deposit notice sent to client');
      }
      setModal(null);
      resetFields();
      // Session-49: fire onMessageSent immediately AND again after 700ms so
      // the fresh coin-cut / deposit-done notice bubble shows without
      // waiting for the socket. On flaky mobile networks the socket emit
      // sometimes reaches the dashboard (chat list preview updates) but
      // not the open chat, forcing the user to leave-and-reopen. The delayed
      // second refetch is a cheap safety net.
      if (onMessageSent) {
        onMessageSent();
        setTimeout(() => { try { onMessageSent(); } catch (_) {} }, 700);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed';
      toast.error(detail);
    } finally { setBusy(false); }
  };

  const formNav = useArrowKeyFormNav(() => { if (!busy) submit(); });
  const utrCheckTimer = useRef(null);

  // Role-based visibility:
  // - Admin (super_admin/admin/superadmin): sees both (based on chat_type; if chat_type unknown, defaults to both)
  // - deposit_handler: ADMIN-LIKE — sees both buttons (Coin Cut in withdraw AND Deposit in deposit)
  // - withdrawal_handler: only Coin Cut button, only if chat_type === 'withdrawal'
  // - support/inquiry handlers: no buttons
  const roleLower = String(actorRole || '').toLowerCase();
  const isAdmin = roleLower === 'super_admin' || roleLower === 'admin' || roleLower === 'superadmin';
  const isDepositHandler = roleLower === 'deposit_handler';
  const isWithdrawalHandler = roleLower === 'withdrawal_handler';
  const chatTypeLower = String(chatType || '').toLowerCase();
  const isDepositChat = chatTypeLower === 'deposit';
  const isWithdrawChat = chatTypeLower === 'withdrawal' || chatTypeLower === 'withdraw';
  const isSupportOrInquiry = chatTypeLower === 'support' || chatTypeLower === 'inquiry';
  // Session-48h: pure enquiry chats only get Mark-as-Solved (no manual
  // "Create Enquiry" button — enquiries auto-start on first customer msg).
  const isInquiryChat = chatTypeLower === 'inquiry';

  // deposit_handler is treated LIKE admin (Feature 1: admin-like power)
  const canBoth = isAdmin || isDepositHandler;

  // Admin/deposit_handler fallback: if chat_type is unknown/missing AND it's NOT support/inquiry, show BOTH buttons
  const unknownChatBoth = canBoth && !isDepositChat && !isWithdrawChat && !isSupportOrInquiry;

  const showCoinCut = (isWithdrawChat && (canBoth || isWithdrawalHandler)) || unknownChatBoth;
  const showDeposit = (isDepositChat && canBoth) || unknownChatBoth;

  if (typeof window !== 'undefined' && !window.__chatHeaderActionsLogged) {
    window.__chatHeaderActionsLogged = true;
    // eslint-disable-next-line no-console
    console.log('[ChatHeaderActions] role=', actorRole, 'chatType=', chatType, 'showCoinCut=', showCoinCut, 'showDeposit=', showDeposit);
  }

  // Session-48c: Support/Inquiry chats no longer have Coin Cut / Deposit but
  // still need the header strip so we can show a Mark-as-Solved primary +
  // Create Request secondary button. Compute the primary "context" button
  // (only one of these three ever renders on the left):
  //   Withdraw → Coin Cut · Deposit → Deposit · Support/Inquiry → Mark Solved
  const showMarkSolved = isSupportOrInquiry && typeof onMarkSolved === 'function';
  const canCreateRequest = typeof onCreateRequest === 'function' && !isInquiryChat && (isDepositChat || isWithdrawChat || isSupportOrInquiry);

  // Flag buttons should always be visible for admins/handlers with a customer
  const canFlag = !!customerId && (isAdmin || isDepositHandler || isWithdrawalHandler || roleLower.endsWith('_handler'));

  if (!showCoinCut && !showDeposit && !showMarkSolved && !canCreateRequest && !canFlag) return null;

  const renderFields = () => (
    <>
      <div>
        <label className="text-xs text-gray-600 mb-1 block font-semibold">Amount (₹) *</label>
        <input
          autoFocus
          type="text"
          inputMode="numeric"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9,]/g, ''))}
          placeholder="e.g. 2000"
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-base font-semibold"
          data-testid="chat-header-amount-input"
        />
      </div>

      {modal === 'coin_cut' && (
        <>
          {!bankSaved && (
            <div className="border border-amber-200 rounded-lg p-2.5 bg-amber-50/70 space-y-2" data-testid="coincut-bank-block">
              <div className="flex items-center justify-between gap-2">
                <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">Client Bank Details</label>
                <div className="flex items-center gap-2">
                  {bankLoading ? <span className="text-[10px] text-slate-400">loading…</span> : <span className="text-[10px] text-amber-600 font-medium">not saved</span>}
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const raw = await navigator.clipboard.readText();
                        if (!raw || !raw.trim()) { toast.error('Clipboard is empty'); return; }
                        const parsed = parseBankFromPaste(raw);
                        const keys = Object.keys(parsed);
                        if (keys.length === 0) { toast.error('No bank details found in clipboard'); return; }
                        // Merge — only overwrite empty fields so we don't wipe manual edits.
                        setBank((b) => {
                          const next = { ...b };
                          for (const k of keys) if (!next[k]) next[k] = parsed[k];
                          return next;
                        });
                        toast.success(`Filled ${keys.length} field${keys.length === 1 ? '' : 's'} from clipboard`);
                      } catch (err) {
                        console.error('Clipboard paste failed:', err);
                        toast.error('Clipboard access denied — allow permission and try again');
                      }
                    }}
                    className="px-2 py-0.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-wide shadow-sm flex items-center gap-1"
                    data-testid="chat-header-coincut-paste-btn"
                    title="Paste form from clipboard — auto-fills fields"
                  >
                    <span>📋</span> Paste
                  </button>
                </div>
              </div>
              <input type="text" value={bank.account_holder_name}
                onChange={(e) => setBank((b) => ({ ...b, account_holder_name: e.target.value }))}
                placeholder="A/c Holder Name"
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs bg-white"
                data-testid="chat-header-coincut-bank-holder" />
              <input type="text" value={bank.bank_account_number}
                onChange={(e) => setBank((b) => ({ ...b, bank_account_number: e.target.value }))}
                placeholder="Bank Account No"
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs bg-white"
                data-testid="chat-header-coincut-bank-account" />
              <div className="grid grid-cols-2 gap-2">
                <input type="text" value={bank.ifsc_code}
                  onChange={(e) => setBank((b) => ({ ...b, ifsc_code: e.target.value.toUpperCase() }))}
                  placeholder="IFSC"
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs bg-white"
                  data-testid="chat-header-coincut-bank-ifsc" />
                <input type="text" value={bank.bank_name}
                  onChange={(e) => setBank((b) => ({ ...b, bank_name: e.target.value }))}
                  placeholder="Bank Name"
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs bg-white"
                  data-testid="chat-header-coincut-bank-name" />
              </div>
              <input type="text" value={bank.upi_id}
                onChange={(e) => setBank((b) => ({ ...b, upi_id: e.target.value }))}
                placeholder="UPI ID"
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs bg-white"
                data-testid="chat-header-coincut-bank-upi" />
            </div>
          )}
          {bankSaved && (
            <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 flex items-center gap-2" data-testid="coincut-bank-saved-note">
              <span>✓</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span>Client bank saved · will be used automatically for this coin cut.</span>
                  {(() => {
                    const bVer = bankVerified.bank;
                    const acnumMatches = bVer && bVer.value && (bVer.value.bank_account_number || '') === (bank.bank_account_number || '');
                    if (bVer?.verified && acnumMatches) {
                      const tip = `Verified · used in ${bVer.verified_count || 1} coin cut${(bVer.verified_count || 1) === 1 ? '' : 's'}${bVer.verified_by_name ? ' · by ' + bVer.verified_by_name : ''}`;
                      return (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-blue-100 border border-blue-300 text-blue-700 text-[9px] font-bold uppercase"
                          title={tip}
                          data-testid="coincut-bank-verified-badge"
                        >
                          <span>✓</span> Verified{bVer.verified_count > 1 ? ` ×${bVer.verified_count}` : ''}
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>
                {(bank.bank_account_number || bank.upi_id) && (
                  <div className="text-[10px] text-emerald-800/80 truncate mt-0.5">
                    {bank.account_holder_name && <span>{bank.account_holder_name} · </span>}
                    {bank.bank_account_number && <span className="font-mono">A/c {bank.bank_account_number}</span>}
                    {bank.upi_id && <span className="font-mono"> · {bank.upi_id}</span>}
                  </div>
                )}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setHistoryPicker({ field: 'bank' })}
                  className="px-2 py-1 rounded bg-indigo-500 hover:bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-wide"
                  data-testid="coincut-change-bank-btn"
                  title="Pick a different saved bank account"
                >Change</button>
              </div>
            </div>
          )}
          <div>
            <label className="text-xs text-gray-600 mb-1 block font-semibold">Reference / Description <span className="text-gray-400">(optional)</span></label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. Ref#12345 or note"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="chat-header-coincut-reference-input"
            />
          </div>
        </>
      )}

      {modal === 'deposit' && (
        <>
          <div>
            <label className="text-xs text-gray-600 mb-1 block font-semibold">
              UTR Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={utrNumber}
              onChange={(e) => {
                const val = e.target.value.toUpperCase();
                setUtrNumber(val);
                // Debounced duplicate check — fires 400ms after typing stops
                if (utrCheckTimer.current) clearTimeout(utrCheckTimer.current);
                if (val.trim().length >= 6) {
                  utrCheckTimer.current = setTimeout(async () => {
                    try {
                      const { data } = await axios.get(`${API}/utr/check`, { params: { utr: val.trim() } });
                      setUtrCheck(data);
                    } catch { setUtrCheck({ exists: false, matches: [] }); }
                  }, 400);
                } else {
                  setUtrCheck({ exists: false, matches: [] });
                }
              }}
              placeholder="e.g. 123456789012"
              className={`w-full px-3 py-2 border rounded-lg text-sm ${utrCheck.exists ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
              data-testid="chat-header-deposit-utr-input"
            />
            {utrCheck.exists && (
              <button
                type="button"
                onClick={() => setShowUtrTrail(true)}
                className={`mt-1 w-full text-left text-[11px] rounded px-2 py-1 font-semibold border transition ${utrBlockMode === 'block' ? 'bg-red-100 text-red-800 border-red-400 hover:bg-red-200' : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'}`}
                data-testid="utr-duplicate-warning"
              >
                {utrBlockMode === 'block' ? '🚫' : '⚠️'} UTR already used on {utrCheck.matches.length} entry(s){utrBlockMode === 'block' ? ' — BLOCKED' : ''}
                <span className="ml-1 text-[10px] underline opacity-80">view trail →</span>
              </button>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1 block font-semibold">
              Received From (customer bank / UPI) <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={receivedFrom}
              onChange={(e) => setReceivedFrom(e.target.value)}
              placeholder="e.g. SBI ****5678 or customer@ybl"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="chat-header-deposit-received-from"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1 block font-semibold">
              Received In (our bank / UPI) <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={receivedIn}
              onChange={(e) => setReceivedIn(e.target.value)}
              placeholder="e.g. HDFC ****1234 or our-upi@paytm"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="chat-header-deposit-received-in"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1 block font-semibold">
              Description <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. UPI transfer for weekend bonus"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="chat-header-deposit-description-input"
            />
          </div>
        </>
      )}

      <div className="flex gap-2 pt-1">
        <button onClick={() => { setModal(null); resetFields(); }} disabled={busy} className="flex-1 py-2 border rounded-lg text-sm">Cancel</button>
        <button
          onClick={submit}
          disabled={busy || (modal === 'deposit' && utrBlockMode === 'block' && utrCheck.exists)}
          className="flex-1 py-2 rounded-lg text-white font-bold text-sm shadow disabled:opacity-50"
          style={{ background: modal === 'coin_cut' ? '#f59e0b' : '#3b82f6' }}
          data-testid="chat-header-amount-submit"
        >
          {busy ? 'Sending…' : (modal === 'deposit' && utrBlockMode === 'block' && utrCheck.exists ? '🚫 Duplicate UTR' : 'Send')}
        </button>
      </div>
    </>
  );

  return (
    <>
      <div className="flex gap-2 px-3 py-1.5 bg-white/95 border-b border-gray-100 items-center" data-testid="chat-header-actions">
        {/* Session-48g: Layout SWAPPED — Create ___ Form on LEFT (was RIGHT),
            action button (Coin Cut / Deposit / Mark Solved) now on RIGHT.
            Label also renamed: "Create ___ Request" → "Create ___ Form".
            Configurable label priority: `hdrLabels.<slot>` overrides these
            defaults when Admin has set a custom string in Settings → Chat
            Header Labels. */}
        {canCreateRequest && (
          <button
            onClick={() => onCreateRequest()}
            data-testid="header-create-request-btn"
            className="flex-1 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-sm"
          >
            <span>➕</span> {(hdrLabels.left_create) || `Create ${isDepositChat ? 'Deposit Form' : isWithdrawChat ? 'Withdraw Form' : 'Support R'}`}
          </button>
        )}
        {showCoinCut && (
          <button
            onClick={() => { resetFields(); openPendingChooser('coin_cut'); }}
            data-testid="header-coin-cut-btn"
            disabled={pendingLoading}
            className="flex-1 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-sm disabled:opacity-60"
          >
            <span>✂️</span> {pendingLoading && pendingKind === 'coin_cut' ? 'Loading…' : (hdrLabels.right_action || 'Coin Cut')}
          </button>
        )}
        {showDeposit && (
          <button
            onClick={() => { resetFields(); openPendingChooser('deposit'); }}
            data-testid="header-deposit-btn"
            disabled={pendingLoading}
            className="flex-1 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-sm disabled:opacity-60"
          >
            <span>💰</span> {pendingLoading && pendingKind === 'deposit' ? 'Loading…' : (hdrLabels.right_action || 'Deposit')}
          </button>
        )}
        {showMarkSolved && (
          <button
            onClick={handleMarkSolvedClick}
            disabled={supportChooserLoading}
            data-testid="header-mark-solved-btn"
            className="flex-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-sm disabled:opacity-60"
          >
            <span>✅</span> {supportChooserLoading ? 'Checking…' : (hdrLabels.right_action || 'Mark as Solved')}
          </button>
        )}
        {canFlag && (
          <div className="flex gap-1 ml-auto flex-shrink-0" data-testid="chat-header-flags">
            <button
              onClick={() => setFlag(customerFlag === 'red' ? null : 'red')}
              disabled={flagBusy}
              title={customerFlag === 'red' ? 'Remove suspicious flag' : 'Mark as Suspicious'}
              data-testid="header-red-flag-btn"
              className={`w-8 h-8 rounded-full flex items-center justify-center border ${customerFlag === 'red' ? 'bg-red-100 border-red-500' : 'bg-white border-gray-200 hover:bg-red-50'}`}
            ><Flag size={14} fill="#dc2626" color="#dc2626" strokeWidth={2} /></button>
            <button
              onClick={() => setFlag(customerFlag === 'green' ? null : 'green')}
              disabled={flagBusy}
              title={customerFlag === 'green' ? 'Remove premium flag' : 'Mark as Premium'}
              data-testid="header-green-flag-btn"
              className={`w-8 h-8 rounded-full flex items-center justify-center border ${customerFlag === 'green' ? 'bg-green-100 border-green-500' : 'bg-white border-gray-200 hover:bg-green-50'}`}
            ><Flag size={14} fill="#16a34a" color="#16a34a" strokeWidth={2} /></button>
          </div>
        )}
      </div>

      {modal && (
        <FloatingResizablePanel
          title={modal === 'coin_cut' ? '✂️ Coin Cut Amount' : '💰 Deposit Amount'}
          headerColor={modal === 'coin_cut' ? '#f59e0b' : '#3b82f6'}
          onClose={() => { setModal(null); resetFields(); }}
          storageKey={`chat-amount-panel-${modal}`}
          initialWidth={Math.min(380, window.innerWidth - 24)}
          initialHeight={460}
          testId="chat-header-amount-modal"
        >
          <div className="p-3 space-y-3" {...formNav}>
            {renderFields()}
          </div>
        </FloatingResizablePanel>
      )}

      <UtrTrailModal
        utr={utrNumber.trim()}
        open={showUtrTrail}
        onClose={() => setShowUtrTrail(false)}
        actorRole={actorRole}
        initialMatches={utrCheck.matches}
      />

      {/* Coin-Cut Change flow — pick a different saved bank / UPI from history */}
      {historyPicker && customerId && (
        <ClientFieldHistoryPanel
          customerId={customerId}
          field={historyPicker.field}
          mode="picker"
          actorId={actorId}
          actorRole={actorRole}
          onClose={() => setHistoryPicker(null)}
          onSelected={(entry) => {
            const v = entry?.value || {};
            if (historyPicker.field === 'bank') {
              setPickedBankEntryId(entry.id);
              setBank((b) => ({
                ...b,
                account_holder_name: v.account_holder_name ?? b.account_holder_name,
                bank_account_number: v.bank_account_number ?? b.bank_account_number,
                ifsc_code: v.ifsc_code ?? b.ifsc_code,
                bank_name: v.bank_name ?? b.bank_name,
              }));
              // Once handler picks a bank, treat the block as "saved" so the
              // paste form doesn't reappear and the compact confirmation
              // banner shows the freshly-picked values.
              setBankSaved(true);
            } else if (historyPicker.field === 'upi') {
              setPickedUpiEntryId(entry.id);
              setBank((b) => ({ ...b, upi_id: v.upi_id ?? b.upi_id }));
            }
            setHistoryPicker(null);
          }}
        />
      )}

      {/* Session-43: Pending Service Request chooser — draggable sheet.
          Opens at ~30% from top (not stuck to the keyboard on mobile) and
          user can drag the header bar to reposition anywhere. Long request
          lists scroll inside the fixed content area. */}
      {pendingKind && pendingList.length > 0 && (
        <DraggablePendingChooser
          kind={pendingKind}
          items={pendingList}
          onClose={() => setPendingKind(null)}
          onApprove={approveRequest}
          onCancel={(req) => { setCancelForReq(req); setCancelReason(''); }}
        />
      )}

      {/* Support Mark-as-Solved — Approve step. Shows the open Support
          request(s); Approve → opens the description dialog → mark-solved
          closes the request + clears the S/PT toggle. */}
      {supportChooser && supportChooser.length > 0 && (
        <div className="fixed inset-0 bg-black/50 z-[120]"
             onClick={() => setSupportChooser(null)}
             data-testid="support-approve-backdrop">
          <div onClick={(e) => e.stopPropagation()}
               className="absolute left-1/2 -translate-x-1/2 bg-white rounded-xl w-[95%] max-w-md shadow-2xl flex flex-col"
               style={{ top: '20%', maxHeight: '65vh' }}
               data-testid="support-approve-modal">
            <div className="px-4 pt-4 pb-1">
              <h3 className="font-bold text-base mb-1">✅ Approve Support Request</h3>
              <p className="text-[11px] text-slate-500">Approve a request below — then add a note to mark it solved.</p>
            </div>
            <div className="space-y-2 overflow-y-auto px-4 flex-1">
              {supportChooser.map((req) => (
                <div key={req.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid={`support-approve-item-${req.request_no}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-800 text-sm">{req.request_no}</span>
                    <span className="text-[10px] text-slate-500 uppercase tracking-wide">support</span>
                  </div>
                  {req.label && <div className="text-[12px] text-slate-700 mt-0.5">{req.label}</div>}
                  {req.reference && <div className="text-[11px] text-slate-500 mt-0.5">Ref: {req.reference}</div>}
                  <button
                    onClick={() => { setSupportChooser(null); onMarkSolved(req.id); }}
                    className="mt-2 w-full py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold"
                    data-testid={`support-approve-${req.request_no}`}
                  >✓ Approve &amp; Continue</button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 p-4 pt-3">
              <button
                onClick={() => setSupportChooser(null)}
                className="flex-1 py-2 border border-slate-300 rounded-lg text-sm"
                data-testid="support-approve-cancel"
              >Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Session-41: Cancel Reason modal (free-text, required). */}
      {cancelForReq && (
        <DraggableModal
          open={!!cancelForReq}
          onClose={() => { if (!cancelBusy) { setCancelForReq(null); setCancelReason(''); } }}
          testId="sr-cancel-reason-modal"
          title={`❌ Cancel ${cancelForReq.request_no}`}
        >
          <div>
            <p className="text-[11px] text-slate-500 mb-3">Add a short reason — visible to everyone in this chat.</p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason"
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:outline-none focus:border-red-400"
              data-testid="sr-cancel-reason-input"
            />
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => { setCancelForReq(null); setCancelReason(''); }}
                disabled={cancelBusy}
                className="flex-1 py-2 border border-slate-300 rounded-lg text-sm"
              >Back</button>
              <button
                onClick={submitCancel}
                disabled={cancelBusy || !cancelReason.trim()}
                className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50"
                data-testid="sr-cancel-reason-submit"
              >{cancelBusy ? 'Cancelling…' : 'Cancel Request'}</button>
            </div>
          </div>
        </DraggableModal>
      )}
    </>
  );
};

export default ChatHeaderActions;
